import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Bot,
  Download,
  Link as LinkIcon,
  Copy,
  Check,
  FileCode,
  Sparkles,
} from 'lucide-react';

const RESOURCES = [
  {
    id: 'llms-txt',
    name: 'llms.txt',
    path: '/llms.txt',
    description:
      'Standard discovery index. Point Cursor, Copilot, Claude Code or any AI tool to this URL first — it links to every other brand file.',
    featured: true,
  },
  {
    id: 'brand-guide',
    name: 'Complete Brand Guide',
    path: '/jackstone-brand-guide.md',
    description:
      'Single comprehensive file containing every rule. Best for one-shot ingestion when feeding context to an LLM.',
    featured: true,
  },
  {
    id: 'colors',
    name: 'Colour Palette',
    path: '/brand/colors.md',
    description: 'Hex / RGB values, usage ratios, WCAG-checked pairings.',
  },
  {
    id: 'typography',
    name: 'Typography',
    path: '/brand/typography.md',
    description: 'Bebas Neue + Montserrat hierarchy, weights, sizes.',
  },
  {
    id: 'logo',
    name: 'Logo Guidelines',
    path: '/brand/logo.md',
    description: 'Approved files, clear space, minimum sizes.',
  },
  {
    id: 'voice',
    name: 'Tone of Voice',
    path: '/brand/voice.md',
    description: 'Voice framework, words to use, words to avoid.',
  },
  {
    id: 'imagery',
    name: 'Imagery & Photography',
    path: '/brand/imagery.md',
    description: 'Photo style, colour treatment, what to avoid.',
  },
  {
    id: 'dos-donts',
    name: "Do's & Don'ts",
    path: '/brand/dos-donts.md',
    description: 'Quick visual checklist for every application.',
  },
];

export const AIResources = () => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const [copiedId, setCopiedId] = useState(null);

  const fullUrl = (path) => `${baseUrl}${path}`;

  const flashCopied = (id) => {
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const copyUrl = async (resource) => {
    try {
      await navigator.clipboard.writeText(fullUrl(resource.path));
      flashCopied(`url-${resource.id}`);
      toast.success(`${resource.name} URL copied`);
    } catch (e) {
      toast.error('Clipboard unavailable — long-press the URL to copy manually');
    }
  };

  const copyMarkdown = async (resource) => {
    try {
      const res = await fetch(resource.path);
      const text = await res.text();
      await navigator.clipboard.writeText(text);
      flashCopied(`md-${resource.id}`);
      toast.success(`${resource.name} markdown copied`);
    } catch (e) {
      toast.error('Copy failed — try the download button instead');
    }
  };

  const download = (resource) => {
    const link = document.createElement('a');
    link.href = resource.path;
    const filename = resource.path.split('/').pop();
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`${resource.name} downloaded`);
  };

  const cursorRuleSnippet = `# .cursorrules
You are working on a Jackstone project. ALWAYS follow the official brand guidelines.

Brand reference (fetch and read these first):
- ${fullUrl('/llms.txt')}
- ${fullUrl('/jackstone-brand-guide.md')}

Rules you MUST never violate:
- Typography: Bebas Neue (H1) + Montserrat 500/600/700 only. No Inter, Roboto, Arial.
- Colours: Jackstone Blue #39678F dominant, no off-palette colours.
- Logo: Use /logo-blue.png or /logo-white.png only. Preserve 7.244:1 aspect ratio.
- Voice: Expert, direct, reliable. No hype words. No emoji.`;

  return (
    <Card
      className="p-8 mt-12 border-jackstone-blue/30 bg-gradient-to-br from-background to-light-blue/30"
      data-testid="ai-resources-card"
    >
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-jackstone-blue/10">
          <Bot className="h-6 w-6 text-jackstone-blue" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-2xl font-heading font-semibold text-jackstone-blue">
              AI &amp; Developer Resources
            </h3>
            <Badge tone="jackstone-blue" fill="soft" className="gap-1">
              <Sparkles className="h-3 w-3" />
              <span>New</span>
            </Badge>
          </div>
          <p className="text-sm text-subtle max-w-2xl">
            Machine-readable brand rules for AI coding tools (Cursor, GitHub Copilot, Claude Code,
            ChatGPT, etc.) and external sites. Feed these markdown files to any LLM to keep new
            projects strictly on-brand. CORS-friendly URLs — fetch from anywhere.
          </p>
        </div>
      </div>

      <div className="grid gap-3 mb-6">
        {RESOURCES.map((resource) => {
          const urlCopied = copiedId === `url-${resource.id}`;
          const mdCopied = copiedId === `md-${resource.id}`;
          return (
            <div
              key={resource.id}
              className={`rounded-lg border p-4 transition-smooth ${
                resource.featured
                  ? 'border-jackstone-blue/40 bg-light-blue/40'
                  : 'border-border bg-background'
              }`}
              data-testid={`ai-resource-${resource.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                  <FileCode className="h-5 w-5 text-jackstone-blue mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-heading font-semibold text-body">
                        {resource.name}
                      </span>
                      {resource.featured && (
                        <Badge tone="jackstone-blue" fill="soft" className="text-xs">
                          Recommended
                        </Badge>
                      )}
                      <code className="text-xs text-subtle font-mono">
                        {resource.path}
                      </code>
                    </div>
                    <p className="text-xs text-subtle leading-relaxed">
                      {resource.description}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 md:flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyUrl(resource)}
                    className="gap-2 text-xs"
                    data-testid={`copy-url-${resource.id}`}
                  >
                    {urlCopied ? (
                      <>
                        <Check className="h-3.5 w-3.5" />
                        Copied
                      </>
                    ) : (
                      <>
                        <LinkIcon className="h-3.5 w-3.5" />
                        Copy URL
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyMarkdown(resource)}
                    className="gap-2 text-xs"
                    data-testid={`copy-markdown-${resource.id}`}
                  >
                    {mdCopied ? (
                      <>
                        <Check className="h-3.5 w-3.5" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" />
                        Copy MD
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => download(resource)}
                    className="gap-2 text-xs"
                    data-testid={`download-md-${resource.id}`}
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-lg bg-light-blue border border-border p-4">
        <div className="flex items-center justify-between gap-3 mb-2">
          <h4 className="text-sm font-heading font-semibold text-body">
            Quick setup for AI coding tools
          </h4>
          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(cursorRuleSnippet);
                flashCopied('snippet');
                toast.success('Snippet copied — paste into .cursorrules or Copilot Instructions');
              } catch (e) {
                toast.error('Clipboard unavailable — select and copy the snippet manually');
              }
            }}
            className="gap-2 text-xs"
            data-testid="copy-cursorrules"
          >
            {copiedId === 'snippet' ? (
              <>
                <Check className="h-3.5 w-3.5" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy snippet
              </>
            )}
          </Button>
        </div>
        <p className="text-xs text-subtle mb-3">
          Paste the snippet below into <code className="font-mono">.cursorrules</code>,{' '}
          <code className="font-mono">CLAUDE.md</code>,{' '}
          <code className="font-mono">.github/copilot-instructions.md</code>, or any equivalent
          AI-context file in your project.
        </p>
        <pre
          className="text-xs font-mono bg-background border border-border rounded p-3 overflow-x-auto whitespace-pre"
          data-testid="cursorrules-snippet"
        >
          {cursorRuleSnippet}
        </pre>
      </div>
    </Card>
  );
};
